import { Fragment, memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./PaymentCouponItem.scss";
import Separator from "app/pages/.shared/Separator";
import { FormattedDate, FormattedMessage } from "react-intl";
import { Amount } from "app/components/ui/Amount/Amount";
import { SPONSORSHIP_SOURCES } from "app/constants";

const PaymentCouponItem = ({
	title,
	friendEmail,
	conditions,
	isApplicable,
	disabled,
	creationDate,
	maxUseDate,
	amount,
	usableAmount,
	usageRule,
	balance,
	uses,
	source,
}) => {
	const clazz = classNames({
		"payment-coupon-item": true,
		"payment-coupon-item--disabled": disabled,
	});

	const disableAmountComponent = !isApplicable && (
		<FormattedMessage id="payment.coupons.not.applicable.status" />
	);

	let titleComponent = title;

	if (
		source === SPONSORSHIP_SOURCES.FRIEND_SUBSCRIBED ||
		source === SPONSORSHIP_SOURCES.FRIEND_BOOKED ||
		source === SPONSORSHIP_SOURCES.SPONSORSHIP_ACCEPTED
	) {
		titleComponent = (
			<Fragment>
				{title} - <span className="payment-coupon-item__email">{friendEmail}</span>
			</Fragment>
		);
	}

	const remainingBalance = balance - usableAmount;

	return (
		<div className={clazz}>
			<div className="payment-coupon-item__header">
				<div className="payment-coupon-item__data">
					<div className={"payment-coupon-item__title"}>{titleComponent}</div>
					{maxUseDate && amount > 0 && (
						<div className={"payment-coupon-item__status"}>
							<FormattedMessage
								id="payment.coupons.expiration.date.status"
								values={{
									creditAmount: (
										<Amount value={amount} isBold={false} size="small" />
									),
									expireAtDate: (
										<FormattedDate
											value={maxUseDate}
											timeZone="UTC"
											day="2-digit"
											month="2-digit"
											year="numeric"
										/>
									),
								}}
							/>
						</div>
					)}
				</div>
				<div className="payment-coupon-item__amount">
					{disabled ? (
						disableAmountComponent
					) : (
						<Amount value={usableAmount} sign="-" signIsBold={false} size="large" />
					)}
				</div>
			</div>

			<div className="payment-coupon-item__conditions">{conditions}</div>

			{((usableAmount !== balance && uses.length === 0) || uses.length > 0) && !disabled && (
				<Fragment>
					<div className="payment-coupon-item__rule">{usageRule}</div>
					<Separator margin={"wide"} design="full" />

					{creationDate && (
						<div className="payment-coupon-item__details-item">
							<div className="payment-coupon-item__status">
								<FormattedMessage
									id="mycoupons.credit.creation.label"
									values={{
										date: (
											<FormattedDate
												timeZone="UTC"
												day="2-digit"
												month="2-digit"
												year="numeric"
												value={creationDate}
											/>
										),
									}}
								/>
							</div>
							<div className="payment-coupon-item__amount">
								<Amount
									value={amount}
									sign="+"
									signIsBold={false}
									size="small"
									className="payment-coupon-item__detail-amount"
								/>
							</div>
						</div>
					)}

					{uses.map(use => {
						const isRefund = !use.bookingReference;

						return (
							<div key={use.date} className="payment-coupon-item__details-item">
								<div className="payment-coupon-item__status">
									<FormattedMessage
										id={
											isRefund
												? "mycoupons.credit.refund.label"
												: "mycoupons.credit.use.label"
										}
										values={{
											date: (
												<FormattedDate
													timeZone="UTC"
													day="2-digit"
													month="2-digit"
													year="numeric"
													value={use.date}
												/>
											),
										}}
									/>
								</div>
								<div className="payment-coupon-item__amount">
									<Amount
										value={use.amount}
										sign="-"
										signIsBold={false}
										size="small"
										className="payment-coupon-item__detail-amount"
									/>
								</div>
							</div>
						);
					})}

					<div className="payment-coupon-item__details-item">
						<div className="payment-coupon-item__status">
							<FormattedMessage id="mycoupons.credit.usablecredits.today" />
						</div>
						<div className="payment-coupon-item__amount">
							<Amount
								value={usableAmount}
								sign="-"
								signIsBold={false}
								size="small"
								className="payment-coupon-item__detail-amount"
							/>
						</div>
					</div>

					{remainingBalance >= 0 && (
						<div className="payment-coupon-item__details-item">
							<div className="payment-coupon-item__status">
								{disabled ? (
									<FormattedMessage id="mycoupons.credit.balance.remaining.label" />
								) : (
									<FormattedMessage id="mycoupons.credit.balance.label" />
								)}
							</div>
							<div className="payment-coupon-item__amount">
								<Amount
									value={remainingBalance}
									size="small"
									className="payment-coupon-item__detail-amount"
								/>
							</div>
						</div>
					)}
				</Fragment>
			)}
		</div>
	);
};

PaymentCouponItem.propTypes = {
	title: PropTypes.string,
	friendEmail: PropTypes.string,
	conditions: PropTypes.string,
	usageRule: PropTypes.string,
	isApplicable: PropTypes.bool,
	disabled: PropTypes.bool,
	creationDate: PropTypes.number,
	maxUseDate: PropTypes.number,
	amount: PropTypes.number,
	usableAmount: PropTypes.number,
	balance: PropTypes.number,
	source: PropTypes.string,
	uses: PropTypes.arrayOf(
		PropTypes.shape({
			useDate: PropTypes.number,
			amount: PropTypes.number,
		})
	),
};

PaymentCouponItem.defaultProps = {
	uses: [],
};

export default memo(PaymentCouponItem);
